// ============================================
// BottomNav - shared component: pill + FAB with speed-dial
// ============================================
// Extracted from Layout.tsx so the Profile redesign (and future page
// redesigns) can consume it as a proper component instead of inline markup.
// Styles live in Layout.css under .tab-bar-* / .tab-fab-* / .speed-dial-*
// / .tab-center-hint - coupled to sibling desktop-sidebar rules that
// don't belong to this component. Keep until a dedicated restyle earns
// its own file.
// ============================================

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { hapticImpact } from '../utils/native';
import { useCloudQuery } from '../data/hooks';
import { fetchActiveProtocols } from '../data/cloud';

// Feature flag: the "Import Protocol" speed-dial entry (video/photo
// protocol analysis at /library/analyze) is hidden for the initial
// launch to keep the FAB menu simple for new users. The feature and its
// route are fully intact - flip this to true to bring the button back.
// (Hidden 2026-07-07.)
const SHOW_IMPORT_PROTOCOL = false;

interface Tab {
  path: string;
  label: string;
  icon: React.ReactNode;
}

const HomeIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const ProtocolIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <line x1="16" y1="13" x2="8" y2="13"/>
    <line x1="16" y1="17" x2="8" y2="17"/>
  </svg>
);

const CalculatorIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="4" y="2" width="16" height="20" rx="2.5"/>
    <rect x="7" y="4.5" width="10" height="3" rx="0.75"/>
    <rect x="7" y="10" width="2.5" height="2.5" rx="0.5"/>
    <rect x="10.75" y="10" width="2.5" height="2.5" rx="0.5"/>
    <rect x="14.5" y="10" width="2.5" height="2.5" rx="0.5"/>
    <rect x="7" y="14" width="2.5" height="2.5" rx="0.5"/>
    <rect x="10.75" y="14" width="2.5" height="2.5" rx="0.5"/>
    <rect x="14.5" y="14" width="2.5" height="6.5" rx="0.5"/>
    <rect x="7" y="18" width="2.5" height="2.5" rx="0.5"/>
    <rect x="10.75" y="18" width="2.5" height="2.5" rx="0.5"/>
  </svg>
);

const LibraryIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
  </svg>
);

const leftTabs: Tab[] = [
  { path: '/dashboard', label: 'Home', icon: <HomeIcon /> },
  { path: '/protocols', label: 'Protocols', icon: <ProtocolIcon /> },
];

const rightTabs: Tab[] = [
  { path: '/calculator', label: 'Calculator', icon: <CalculatorIcon /> },
  { path: '/library', label: 'Library', icon: <LibraryIcon /> },
];

const allTabs = [...leftTabs, ...rightTabs];

interface BottomNavProps {
  /** Invoked when the user picks "Log Dose" from the speed-dial and has at least one active protocol. Layout owns the DoseLogModal. */
  onLogDose: () => void;
}

export const BottomNav: React.FC<BottomNavProps> = ({ onLogDose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const [showNoProtocolHint, setShowNoProtocolHint] = useState(false);

  const { data: activeProtocols } = useCloudQuery('protocols', fetchActiveProtocols, []);
  const hasProtocols = activeProtocols.length > 0;

  // Close speed dial on route change
  useEffect(() => {
    setSpeedDialOpen(false);
  }, [location.pathname]);

  // Listen for tour-driven speed-dial force-open/close events. The guided
  // tour (TourOverlay) dispatches these so the FAB step's spotlight can
  // cover the plus button AND the expanded 4-option speed-dial as one
  // rect. This doesn't interfere with user-initiated toggling - the user
  // can still tap to open/close manually. When the tour exits (complete
  // or skip) it dispatches a final { open: false } to collapse the menu.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ open: boolean }>).detail;
      if (!detail || typeof detail.open !== 'boolean') return;
      setSpeedDialOpen(detail.open);
    };
    window.addEventListener('prtcl:tour-fab-force-open', handler);
    return () => window.removeEventListener('prtcl:tour-fab-force-open', handler);
  }, []);

  const handleCenterTap = () => {
    hapticImpact('light');
    setSpeedDialOpen((prev) => !prev);
  };

  const handleSpeedDialAction = (action: string) => {
    hapticImpact('light');
    setSpeedDialOpen(false);
    switch (action) {
      case 'log':
        if (hasProtocols) {
          onLogDose();
        } else {
          setShowNoProtocolHint(true);
          setTimeout(() => setShowNoProtocolHint(false), 3000);
        }
        break;
      case 'protocol':
        navigate('/protocols/new');
        break;
      case 'vial':
        navigate('/profile/inventory/add');
        break;
      case 'video':
        navigate('/library/analyze');
        break;
    }
  };

  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/dashboard') return '/dashboard';
    const match = allTabs.find((t) => path.startsWith(t.path));
    return match?.path || '/dashboard';
  };

  const renderTab = (tab: Tab) => {
    const isActive = getActiveTab() === tab.path;
    return (
      <button
        key={tab.path}
        className={`tab-item ${isActive ? 'tab-active' : ''}`}
        onClick={() => navigate(tab.path)}
        aria-label={tab.label}
        aria-current={isActive ? 'page' : undefined}
      >
        <span className="tab-icon">{tab.icon}</span>
        <span className={`tab-label${isActive ? '' : ' tab-label-inactive'}`}>{tab.label}</span>
      </button>
    );
  };

  return (
    <nav className="tab-bar-container" aria-label="Main navigation">
      <nav className="tab-bar">
        {allTabs.map(renderTab)}
      </nav>

      <div className="tab-fab-wrapper" data-tour-spot="fab">
        {speedDialOpen && (
          <>
            <div className="speed-dial-backdrop" onClick={() => setSpeedDialOpen(false)} />
            <div className="speed-dial-menu">
              <button className="speed-dial-item" style={{ animationDelay: '150ms' }} onClick={() => handleSpeedDialAction('vial')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
                </svg>
                <span>Add Vial</span>
              </button>
              <button className="speed-dial-item" style={{ animationDelay: '100ms' }} onClick={() => handleSpeedDialAction('protocol')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                  <line x1="12" y1="18" x2="12" y2="12" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
                <span>Add Protocol</span>
              </button>
              {SHOW_IMPORT_PROTOCOL && (
                <button className="speed-dial-item" style={{ animationDelay: '50ms' }} onClick={() => handleSpeedDialAction('video')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 5 21 5 3" />
                  </svg>
                  <span>Import Protocol</span>
                </button>
              )}
              <button className="speed-dial-item" style={{ animationDelay: '0ms' }} onClick={() => handleSpeedDialAction('log')}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span>Log Dose</span>
              </button>
            </div>
          </>
        )}
        <button
          className={`tab-fab ${speedDialOpen ? 'tab-fab-active' : ''}`}
          onClick={handleCenterTap}
          aria-label={speedDialOpen ? 'Close menu' : 'Quick actions'}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
        {showNoProtocolHint && (
          <div className="tab-center-hint">
            <p className="tab-center-hint-text">Create a protocol first to start logging doses.</p>
            <button className="tab-center-hint-link" onClick={() => { setShowNoProtocolHint(false); navigate('/protocols'); }}>
              Go to Protocols
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default BottomNav;
